import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useCart } from "./CartContext";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import brand13 from "../assets/images/icons/brand13.png";
import locationIcon from "../assets/images/svg/location.svg";

const RestaurantMenu = () => {
  // Existing state variables
  const { ownerId } = useParams();
  const ownerData = ownerId;
  const [userId, setUserId] = useState("");
  const [categoriesWithItems, setCategoriesWithItems] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [sauces, setSauces] = useState([]);
  const [drinks, setDrinks] = useState([]);
  const [showDrinks, setShowDrinks] = useState(true);
  const [showFries, setShowFries] = useState(true);
  const [selectedToppings, setSelectedToppings] = useState({
    salade: false,
    tomato: false,
    onions: false,
  });
  const [sauceLimits, setSauceLimits] = useState({
    M: 1,
    L: 2,
    XL: 3,
  });

  const [frenchFries, setFrenchFries] = useState(false);
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedPickupTime, setSelectedPickupTime] = useState(null);
  const [specialInstructions, setSpecialInstructions] = useState("");

  const { addToCart, cart, removeFromCart } = useCart();
  const navigate = useNavigate();
  const categoryListRef = useRef(null);
  const API_URL = process.env.REACT_APP_API_URL;

  const getMinPickupTime = () => {
    const now = new Date();
    const minPickupDate = new Date(now.getTime() + 15 * 60000);
    const hours = String(minPickupDate.getHours()).padStart(2, "0");
    const minutes = String(minPickupDate.getMinutes()).padStart(2, "0");
    return `${hours}:${minutes}`;
  };

  const handlePickupTimeChange = (event) => {
    const selectedTime = event.target.value;
    const minTime = getMinPickupTime();
    if (selectedTime >= minTime) {
      setSelectedPickupTime(selectedTime);
    } else {
      alert("Please select a pickup time at least 15 minutes from now.");
      setSelectedPickupTime(minTime);
    }
  };

  const handleSpecialInstructionsChange = (event) => {
    setSpecialInstructions(event.target.value);
  };

   useEffect(() => {
      const token = localStorage.getItem("userToken");
      if (token) {
        try {
          const decodedToken = jwtDecode(token);
          setUserId(decodedToken.userId);
        } catch (error) {
          console.error("Error decoding token:", error);
        }
      }
    }, []);

  useEffect(() => {
    const fetchItemsAndSauces = async () => {
      try {
        const [itemsResponse, saucesResponse, drinksResponse] =
          await Promise.all([
            axios.get(`${API_URL}/api/items/${ownerId}`),
            axios.get(
              `${API_URL}/api/extraitems/restaurant/sauces/${ownerId}`
            ),
            axios.get(
              `${API_URL}/api/extraitems/restaurant/drinks/${ownerId}`
            ),
          ]);

        setCategoriesWithItems(itemsResponse.data.items);
        setSauces(saucesResponse.data);
        setDrinks(drinksResponse.data);
      } catch (error) {
        console.error("Error fetching items or sauces:", error);
      }
    };

    fetchItemsAndSauces();
  }, [ownerId]);

  const handleAddToCartClick = (item) => {
    if (!selectedSize) {
      alert("Please select a menu option.");
      return;
    }
    if (!selectedPickupTime) {
      alert("Please select a pickup time.");
      return;
    }
    const minPickupTime = getMinPickupTime();
    if (selectedPickupTime < minPickupTime) {
      alert("Please select a pickup time at least 15 minutes from now.");
      return;
    }

    const size =
      item.sizes.find((s) => s.size === selectedSize) || item.sizes[0];
    const selectedSauceIds = selectedCategory?.selectedSauces || [];
    const selectedSauces = selectedSauceIds.map(
      (sauceId) => sauces[sauceId]
    );

    // Ensure only the selected Fries sauces Ids are passed
    const selectedFriesSauceIds = selectedCategory?.selectedFriesSauces || [];
    const selectedFriesSauces = selectedFriesSauceIds.map(
      (sauceId) => sauces[sauceId]
    );
    const selectedDrinkId = selectedCategory?.selectedDrinks[0] || null;
    const selectedDrink = drinks[selectedDrinkId] || null;

    addToCart({
      _id: item._id,
      itemName: item.itemName,
      itemImg: item.itemImg,
      price: size ? size.menuPrice || size.basePrice : item.sizes[0].basePrice,
      freshlyBaked: item.freshlyBaked || false,
      quantity: 1,
      ownerId: ownerData,
      userId: userId,
      category: item.category,
      size: selectedSize,
      selectedSauces: selectedSauces, // Main sauces (if any)
      selectedToppings,
      frenchFries,
      selectedFriesSauces: selectedFriesSauces, // Only Fries Sauces
      selectedDrink: selectedDrink,
      pickupTime: selectedPickupTime,
      specialInstructions: specialInstructions,
    });

    setSpecialInstructions("");
  };

  const handleCategoryClick = (categoryObject) => {
    setSelectedCategory({
      ...categoryObject,
      selectedSauces: [],
      selectedFriesSauces: [],
      selectedDrinks: [],
    });
  };

  const calculateTotal = () => {
    const subtotal = cart.reduce(
      (total, item) => total + item.price * item.quantity,
      0
    );
    const serviceCharge = 0.2;
    const totalAfterService = subtotal + serviceCharge;
    const transactionCharge = totalAfterService * 0.015 + 0.25;
    const total = totalAfterService + transactionCharge;
    return total.toFixed(2);
  };

  const handleSauceChange = (sauceId) => {
    setSelectedCategory((prevCategory) => {
      if (!prevCategory) return prevCategory;

      const sauceLimit = sauceLimits[selectedSize] || 0;

      const newSauces = prevCategory.selectedSauces?.includes(sauceId)
        ? prevCategory.selectedSauces.filter((id) => id !== sauceId)
        : [...(prevCategory.selectedSauces || []), sauceId];

      if (newSauces.length > sauceLimit) {
        alert(
          `Maximum ${sauceLimit} sauces allowed for size ${selectedSize.toUpperCase()}`
        );
        return {
          ...prevCategory,
          selectedSauces: prevCategory.selectedSauces,
        };
      }

      return { ...prevCategory, selectedSauces: newSauces };
    });
  };

  const handleFriesSauceChange = (sauceId) => {
    setSelectedCategory((prevCategory) => {
      if (!prevCategory) return prevCategory;

      const friesSauceLimit = 2; // Limit to 2 fries sauces

      const newFriesSauces = prevCategory.selectedFriesSauces?.includes(sauceId)
        ? prevCategory.selectedFriesSauces.filter((id) => id !== sauceId)
        : [...(prevCategory.selectedFriesSauces || []), sauceId];

      if (newFriesSauces.length > friesSauceLimit) {
        alert(`Maximum ${friesSauceLimit} sauces allowed for fries.`);
        return {
          ...prevCategory,
          selectedFriesSauces: prevCategory.selectedFriesSauces,
        };
      }

      return { ...prevCategory, selectedFriesSauces: newFriesSauces };
    });
  };

  const handleDrinkChange = (drinkId) => {
    setSelectedCategory((prevCategory) => {
      if (!prevCategory) return prevCategory;

      if (
        prevCategory.selectedDrinks &&
        prevCategory.selectedDrinks[0] === drinkId
      ) {
        return { ...prevCategory, selectedDrinks: [] };
      }
      return { ...prevCategory, selectedDrinks: [drinkId] };
    });
  };

  const handleMenuOptionChange = (item, size, option) => {
    const isSansMenu = option.includes("sansMenu");
    setShowDrinks(!isSansMenu);
    setShowFries(!isSansMenu);

    const sizeValue = option.replace("-sansMenu", "").replace("-avecMenu", "");
    setSelectedSize(sizeValue || "sansMenu");
  };

  const handleToppingChange = (toppingName) => {
    setSelectedToppings((prevToppings) => ({
      ...prevToppings,
      [toppingName]: !prevToppings[toppingName],
    }));
  };

  const handleFrenchFriesChange = () => {
    setFrenchFries(!frenchFries);
  };

  return (
    <>
      <section className="product-banner-section">
        <div className="container">
          <div className="restaurant-box">
            <div className="restaurant-image">
              <img
                className="img-fluid img"
                src={brand13}
                alt="Restaurant Logo"
              />
            </div>
            <div className="restaurant-details">
              <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                <div>
                  <h2 className="restaurant-name">Delicious Boulangerie</h2>
                  <h4 className="restaurant-place">Your City, Country</h4>
                </div>
                <div className="restaurant-description">
                  <div className="distance d-flex align-items-center">
                    <h4 className="text-white shop-time">4.0km</h4>
                    <h4 className="rating-star">
                      <span className="star">
                        <i className="ri-star-s-fill"></i>
                      </span>
                      4.5 (500+ Reviews)
                    </h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="account-section section-b-space pt-0">
        <div className="container">
          <div className="layout-sec">
            <div className="row g-lg-4 g-4">
              <div className="col-lg-8">
                <div className="payment-section">
                  <h3 className="menu-title">Boulangerie Items</h3>
                  <div className="category-item-container">
                    <div className="category-list" ref={categoryListRef}>
                      <h2>Categories</h2>
                      <ul>
                        {Array.isArray(categoriesWithItems) &&
                        categoriesWithItems.length > 0 ? (
                          categoriesWithItems.map((categoryObject) => (
                            <li
                              key={categoryObject._id}
                              onClick={() =>
                                handleCategoryClick(categoryObject)
                              }
                              className={
                                selectedCategory?._id === categoryObject._id
                                  ? "active"
                                  : ""
                              }
                            >
                              {categoryObject.category}
                            </li>
                          ))
                        ) : (
                          <li>No categories available</li>
                        )}
                      </ul>
                    </div>

                    <div className="item-display">
                      {selectedCategory ? (
                        <>
                          <h2>{selectedCategory.category}</h2>
                          <div className="items-grid">
                            {Array.isArray(selectedCategory.items) &&
                            selectedCategory.items.length > 0 ? (
                              selectedCategory.items.map((item) => (
                                <div key={item._id} className="item-card">
                                  <img
                                    src={item.itemImg}
                                    alt={item.itemName}
                                    className="item-image"
                                  />
                                  <h1 className="item-name">{item.itemName}</h1>
                                  <h6>
                                    {item.description && (
                                      <p className="item-description">
                                        {item.description}
                                      </p>
                                    )}
                                  </h6>
                                  {item.sizes.length === 1 ? (
                                    <div className="size-prices">
                                      <label className="item-price">
                                        <input
                                          type="radio"
                                          name={`item-${item._id}`}
                                          value="sansMenu"
                                          onChange={() =>
                                            handleMenuOptionChange(
                                              item,
                                              item.sizes[0],
                                              "sansMenu"
                                            )
                                          }
                                        />
                                        Sans Menu: ₹{item.sizes[0].basePrice}
                                      </label>
                                      {item.menuOptions.withMenu && (
                                        <label className="item-price">
                                          <input
                                            type="radio"
                                            name={`item-${item._id}`}
                                            value="avecMenu"
                                            onChange={() =>
                                              handleMenuOptionChange(
                                                item,
                                                item.sizes[0],
                                                "avecMenu"
                                              )
                                            }
                                          />
                                          Avec Menu: ₹{item.sizes[0].menuPrice}
                                        </label>
                                      )}
                                    </div>
                                  ) : (
                                    <div className="size-prices">
                                      {item.sizes.map((size) => (
                                        <div key={size._id}>
                                          <label className="item-price">
                                            <input
                                              type="radio"
                                              name={`item-${item._id}`}
                                              value={`${size.size}-sansMenu`}
                                              onChange={() =>
                                                handleMenuOptionChange(
                                                  item,
                                                  size,
                                                  `${size.size}-sansMenu`
                                                )
                                              }
                                            />
                                            {size.size.toUpperCase()} - Sans
                                            Menu: ₹{size.basePrice}
                                          </label>
                                          {item.menuOptions.withMenu && (
                                            <label className="item-price">
                                              <input
                                                type="radio"
                                                name={`item-${item._id}`}
                                                value={`${size.size}-avecMenu`}
                                                onChange={() =>
                                                  handleMenuOptionChange(
                                                    item,
                                                    size,
                                                    `${size.size}-avecMenu`
                                                  )
                                                }
                                              />
                                              {size.size.toUpperCase()} - Avec
                                              Menu: ₹{size.menuPrice}
                                            </label>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  )}

                                  <div className="freshly-baked">
                                    {item.freshlyBaked && (
                                      <>
                                        <span className="checkmark">✔</span>
                                        Freshly Baked
                                      </>
                                    )}
                                  </div>
                                  <div className="pickup-time-selection">
                                    <h3>Select Pickup Time</h3>
                                    <div className="pickup-time-selection">
                                      <h3>Select Pickup Time</h3>
                                      <input
                                        type="time"
                                        id="pickupTime"
                                        value={selectedPickupTime}
                                        onChange={handlePickupTimeChange}
                                        min={getMinPickupTime()}
                                      />
                                    </div>
                                  </div>
                                  <div className="special-instructions">
                                    <h3>Special Instructions</h3>
                                    <textarea
                                      value={specialInstructions}
                                      onChange={
                                        handleSpecialInstructionsChange
                                      }
                                      placeholder="Add any special requests here..."
                                    />
                                  </div>
                                  <button
                                    className="add-to-cart-button"
                                    onClick={() => handleAddToCartClick(item)}
                                  >
                                    Add to Cart
                                  </button>
                                </div>
                              ))
                            ) : (
                              <p>No items available</p>
                            )}
                            <div>
                              <div className="toppings-selection">
                                <h3>Select Toppings</h3>
                                <div className="toppings-list">
                                  <label>
                                    <input
                                      type="checkbox"
                                      checked={selectedToppings.salade}
                                      onChange={() =>
                                        handleToppingChange("salade")
                                      }
                                    />
                                    Salade
                                  </label>
                                  <label>
                                    <input
                                      type="checkbox"
                                      checked={selectedToppings.tomato}
                                      onChange={() =>
                                        handleToppingChange("tomato")
                                      }
                                    />
                                    Tomato
                                  </label>
                                  <label>
                                    <input
                                      type="checkbox"
                                      checked={selectedToppings.onions}
                                      onChange={() =>
                                        handleToppingChange("onions")
                                      }
                                    />
                                    Onions
                                  </label>
                                </div>
                              </div>
                              {sauces.length > 0 && (
                                <div className="sauces-selection">
                                  <h3>Select Sauces</h3>
                                  <div className="sauces-list">
                                    {sauces.map((sauce, index) => {
                                      const sauceLimit =
                                        sauceLimits[selectedSize] || 0;
                                      const isSauceLimitReached =
                                        selectedCategory?.selectedSauces
                                          ?.length >= sauceLimit &&
                                        !selectedCategory?.selectedSauces?.includes(
                                          index
                                        );
                                      return (
                                        <div
                                          key={index}
                                          className="sauce-item"
                                        >
                                          <input
                                            type="checkbox"
                                            id={`sauce-${index}`}
                                            checked={
                                              selectedCategory?.selectedSauces?.includes(
                                                index
                                              ) || false
                                            }
                                            onChange={() =>
                                              handleSauceChange(index)
                                            }
                                            disabled={isSauceLimitReached}
                                          />
                                          <label htmlFor={`sauce-${index}`}>
                                            <img
                                              src={sauce.image}
                                              alt={sauce.name}
                                              style={{
                                                width: "50px",
                                                height: "50px",
                                              }}
                                            />
                                            {sauce.name}
                                          </label>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                              {showFries && (
                                <div className="french-fries-selection">
                                  <h3>French Fries</h3>
                                  <label>
                                    <input
                                      type="checkbox"
                                      checked={frenchFries}
                                      onChange={handleFrenchFriesChange}
                                    />
                                    Add French Fries
                                  </label>
                                </div>
                              )}
                              {showFries && sauces.length > 0 && (
                                <div className="fries-sauces-selection">
                                  <h3>Sauce for French Fries (Choose up to 2)</h3>
                                  <div className="fries-sauces-list">
                                    {sauces.map((sauce, index) => (
                                      <div
                                        key={index}
                                        className="fries-sauce-item"
                                      >
                                        <input
                                          type="checkbox"
                                          id={`fries-sauce-${index}`}
                                          checked={
                                            selectedCategory?.selectedFriesSauces?.includes(
                                              index
                                            ) || false
                                          }
                                          onChange={() =>
                                            handleFriesSauceChange(index)
                                          }

                                          disabled={
                                            selectedCategory?.selectedFriesSauces
                                              ?.length >= 2 &&
                                            !selectedCategory?.selectedFriesSauces?.includes(
                                              index
                                            )
                                          }
                                        />
                                        <label htmlFor={`fries-sauce-${index}`}>
                                          <img
                                            src={sauce.image}
                                            alt={sauce.name}
                                            style={{
                                              width: "50px",
                                              height: "50px",
                                            }}
                                          />
                                          {sauce.name}
                                        </label>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {showDrinks && drinks.length > 0 && (
                                <div className="drinks-selection">
                                  <h3>Select Drinks</h3>
                                  <div className="drinks-list">
                                    {drinks.map((drink, index) => (
                                      <div
                                        key={index}
                                        className="drink-item"
                                      >
                                        <input
                                          type="radio"
                                          id={`drink-${index}`}
                                          name="drinkSelection"
                                          checked={
                                            selectedCategory?.selectedDrinks &&
                                            selectedCategory.selectedDrinks[0] ===
                                              index
                                          }
                                          onChange={() =>
                                            handleDrinkChange(index)
                                          }
                                        />
                                        <label htmlFor={`drink-${index}`}>
                                          <img
                                            src={drink.image}
                                            alt={drink.name}
                                            style={{
                                              width: "50px",
                                              height: "50px",
                                            }}
                                          />
                                          {drink.name}
                                        </label>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </>
                      ) : (
                        <p>Select a category to view items.</p>
                      )}
                    </div>
                  </div>

                  <Link to="/cart" className="view-cart-button">
                    <span className="cart-icon"></span>View Cart (
                    {cart.length})
                  </Link>
                </div>
              </div>

              <div className="col-lg-4">
                <div className="order-summery-section sticky-top">
                  <div className="checkout-detail">
                    <h3 className="fw-semibold dark-text checkout-title">
                      Order Summary
                    </h3>
                    <ul>
                      {cart.map((item) => (
                        <li key={item._id}>
                          <div className="d-flex justify-content-between">
                            <p>{item.itemName}</p>
                            <p>{item.quantity}</p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="total-price-box">
                    <h5>Total Price</h5>
                    <p>₹{calculateTotal()}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default RestaurantMenu;
